/** Общее для вкладок доски: адрес страницы с отбором, цвета статусов и приоритетов, «кто взял» */
import {
  BOARD_CHAT_RE,
  BOARD_STATUS_TITLES,
  type BoardPrio,
  type BoardStatus,
  type BoardTask,
  boardTakerLabel,
} from '@epc/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../lib/api';
import { relative } from '../../lib/format';
import { Badge, type Tone, toast } from '../../ui/feedback';

export type BoardTab = 'tasks' | 'chats' | 'access';
export const BOARD_TAB_TITLES: Record<BoardTab, string> = {
  tasks: 'Задачи',
  chats: 'Чаты',
  access: 'Доступ',
};

/** Вкладка, отбор и открытая задача — в адресе: ссылку можно переслать, «назад» возвращает прежний вид */
export interface BoardSearch {
  tab?: 'chats' | 'access';
  chat?: string;
  status?: string;
  taken?: 'free' | 'mine';
  q?: string;
  task?: string;
}

export function validateBoardSearch(s: Record<string, unknown>): BoardSearch {
  const str = (v: unknown, max: number) => (typeof v === 'string' && v.length <= max ? v : undefined);
  const chat = str(s.chat, 32);
  const status = str(s.status, 80);
  return {
    tab: s.tab === 'chats' || s.tab === 'access' ? s.tab : undefined,
    chat: chat && (chat === 'none' || BOARD_CHAT_RE.test(chat)) ? chat : undefined,
    status: status && /^[a-z,]+$/.test(status) ? status : undefined,
    taken: s.taken === 'free' || s.taken === 'mine' ? s.taken : undefined,
    q: str(s.q, 200) || undefined,
    task: str(s.task, 300) || undefined,
  };
}

export const STATUS_TONE: Record<BoardStatus, Tone> = {
  new: 'neutral',
  doing: 'info',
  review: 'accent',
  blocked: 'warning',
  done: 'success',
  cancelled: 'neutral',
};

export const PRIO_TONE: Record<BoardPrio, Tone> = {
  П0: 'danger',
  П1: 'warning',
  П2: 'neutral',
  П3: 'neutral',
};

export function StatusBadge({ status }: { status: BoardStatus }) {
  return <Badge tone={STATUS_TONE[status]}>{BOARD_STATUS_TITLES[status]}</Badge>;
}

/** «Взял Сергей Иванов (чат plan) · 5 минут назад» или «Свободна» */
export function takerText(t: Pick<BoardTask, 'takenByName' | 'takenByChat' | 'takenByUserId' | 'takenAt'>) {
  if (!t.takenByUserId) return 'Свободна';
  return `Взял ${boardTakerLabel(t)} · ${relative(t.takenAt)}`;
}

export const isClosed = (s: BoardStatus) => s === 'done' || s === 'cancelled';

/** Обновить всё, что показывает задачи: списки, карточку, чаты */
export function useRefreshBoard() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ['devboard'] });
}

/** Отказ сервера — тостом; «уже взяли» и «изменили» — с обновлением доски */
export function useBoardError() {
  const refresh = useRefreshBoard();
  return (err: unknown) => {
    const text = err instanceof Error ? err.message : 'Не получилось. Повторите.';
    toast(text, 'danger');
    if (err instanceof ApiError && err.status === 409) refresh();
  };
}
